import { Injectable, inject } from '@angular/core';
import type { fhirR4 } from '@smile-cdr/fhirts';
import { DataService } from './data.service';
import cloneDeep from 'lodash/cloneDeep';

export enum OriginSource {
  AUTO = 'auto',
  MANUAL = 'manual',
  OVERRIDE = 'override',
}
@Injectable({
  providedIn: 'root',
})
export class ExtensionService {
  private readonly dataService = inject(DataService);

  static readonly authorExtensionUrl =
    'http://hl7.org/fhir/StructureDefinition/questionnaireresponse-author';
  static readonly originExtensionUrl =
    'http://hl7.org/fhir/us/davinci-dtr/StructureDefinition/information-origin';
  static readonly unitOptionExtensionUrl =
    'http://hl7.org/fhir/StructureDefinition/questionnaire-unitOption';

  autoExtension = [
    {
      extension: [
        {
          url: 'source',
          valueCode: OriginSource.AUTO,
        },
      ],
      url: ExtensionService.originExtensionUrl,
    },
  ];

  addAutoOrigin(qrItem: fhirR4.QuestionnaireResponseItem[] | undefined) {
    qrItem
      ?.filter((item) => {
        if (item.item) {
          this.addAutoOrigin(item.item);
        }
        return item.answer;
      })
      .map((item) =>
        item.answer?.map((answer) => (answer.extension = this.autoExtension)),
      );
  }

  createOriginExtensionWithSource(source: string): fhirR4.Extension[] {
    return [
      {
        extension: [
          {
            url: 'source',
            valueCode: source,
          },
          this.dataService.authorOrigin,
        ],
        url: ExtensionService.originExtensionUrl,
      },
    ];
  }

  createOriginExtension(
    answers: fhirR4.QuestionnaireResponseAnswer[] | undefined,
    repeats = false,
  ): fhirR4.Extension[] {
    if (!answers || answers.length == 0 || repeats) {
      return this.createOriginExtensionWithSource(OriginSource.MANUAL);
    }
    const extension = answers[0].extension;
    const originSource =
      ExtensionService.getOriginSourceBasedOnPrevExtension(extension);
    return this.createOriginExtensionWithSource(originSource);
  }

  /**
   * @param extension list of extensions for QR answer
   * @returns `override` if previous extension was `auto`, `manual` if there was no previous extension,
   * and previous origin source value if no change necessary
   */
  static getOriginSourceBasedOnPrevExtension(
    extension: fhirR4.Extension[] | undefined,
  ): string {
    if (extension) {
      const prevExt =
        ExtensionService.findOriginSourceExtension(extension)?.valueCode;
      if (!prevExt) return OriginSource.MANUAL;
      else if (prevExt === OriginSource.AUTO) {
        return OriginSource.OVERRIDE;
      } else {
        return prevExt;
      }
    } else {
      return OriginSource.MANUAL;
    }
  }

  static updateOriginExtensionIfNecessary(
    answer: fhirR4.QuestionnaireResponseAnswer | undefined,
    originExtension: fhirR4.Extension[],
  ): fhirR4.Extension[] {
    const expectedType = ExtensionService.getOriginSourceBasedOnPrevExtension(
      answer?.extension,
    );
    if (
      ExtensionService.findOriginSourceExtension(originExtension)?.valueCode !==
      expectedType
    ) {
      const retVal = cloneDeep(originExtension);
      const newSourceExt = ExtensionService.findOriginSourceExtension(retVal);
      if (newSourceExt) newSourceExt.valueCode = expectedType;
      return retVal;
    }
    return originExtension;
  }

  static findOriginSourceExtension(
    extension: fhirR4.Extension[] | undefined,
  ): fhirR4.Extension | undefined {
    return extension
      ?.find((ext) => ext.url === ExtensionService.originExtensionUrl)
      ?.extension?.find((innerExt) => innerExt.url === 'source');
  }
}
